import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import GenericModal from "../GenericModal";
import { EWidths } from "../../Utils/Enums";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";

interface AddProductModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientData: any;
}

export default function AddProductModal({
  open,
  onClose,
  onSuccess,
  clientData,
}: AddProductModalProps) {
  const [orderTypes, setOrderTypes] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedOrderType, setSelectedOrderType] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      getData(endpoints.orderTypes).then((res: any) => {
        const list = res?.data || res || [];
        setOrderTypes(Array.isArray(list) ? list : []);
      });
    }
  }, [open]);

  useEffect(() => {
    if (!selectedOrderType) {
      setProducts([]);
      setSelectedProduct(null);
      return;
    }
    setLoadingProducts(true);
    setSelectedProduct(null);
    getData(
      `${endpoints.getProducts}/visible-for-online-enrollment?orderTypeId=${selectedOrderType._id}`
    )
      .then((res: any) => {
        const list = res?.data || res || [];
        setProducts(Array.isArray(list) ? list : []);
      })
      .finally(() => setLoadingProducts(false));
  }, [selectedOrderType]);

  const handleClose = () => {
    setSelectedOrderType(null);
    setSelectedProduct(null);
    setProducts([]);
    setError("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedOrderType || !selectedProduct) return;
    setSubmitting(true);
    setError("");
    try {
      await postData(endpoints.ordersEndpoint, {
        client_id: clientData,
        orderType: selectedOrderType,
        product: selectedProduct,
      });
      handleClose();
      onSuccess();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          Vocabulary.somethingWentWrong
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getPrice = () => {
    if (!selectedProduct) return null;
    const clientWorkPointId =
      clientData?.workPointId?._id || clientData?.workPointId;
    if (clientWorkPointId && selectedProduct.prices?.length) {
      const match = selectedProduct.prices.find((p: any) => {
        const wpId = p.workPointId?._id || p.workPointId;
        return wpId === clientWorkPointId;
      });
      if (match) return match.price;
    }
    return selectedProduct.price ?? null;
  };

  const price = getPrice();

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title={Vocabulary.addNewProductTitle}
      maxWidth={EWidths.SM}
      actions={
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            startIcon={<Close />}
            fullWidth
            color="error"
            disabled={submitting}
            sx={{ borderRadius: "8px" }}
          >
            {Vocabulary.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : <Add />
            }
            fullWidth
            disabled={!selectedOrderType || !selectedProduct || submitting}
            sx={{
              borderRadius: "8px",
              boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}50`,
              "&:hover": {
                boxShadow: (theme) =>
                  `0 6px 20px ${theme.palette.primary.main}66`,
              },
            }}
          >
            {Vocabulary.addNewProduct}
          </Button>
        </Stack>
      }
    >
      <Box display="flex" flexDirection="column" gap={3}>
        <Autocomplete
          options={orderTypes}
          getOptionLabel={(o) => o.name || ""}
          value={selectedOrderType}
          onChange={(_, val) => setSelectedOrderType(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={Vocabulary.selectServiceType}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          )}
        />

        <Autocomplete
          options={products}
          getOptionLabel={(o) => o.name || ""}
          value={selectedProduct}
          onChange={(_, val) => setSelectedProduct(val)}
          disabled={!selectedOrderType || loadingProducts}
          loading={loadingProducts}
          noOptionsText={
            selectedOrderType ? Vocabulary.noResults : Vocabulary.selectServiceType
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={Vocabulary.selectProduct}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingProducts && (
                      <CircularProgress color="inherit" size={18} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {price !== null && (
          <Box
            p={2}
            borderRadius={2}
            sx={{
              background: "rgba(25, 118, 210, 0.06)",
              border: "1px solid rgba(25, 118, 210, 0.2)",
            }}
          >
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {Vocabulary.price}
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary">
              {Number(price).toLocaleString("ro-RO", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              {Vocabulary.currency}
            </Typography>
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </Box>
    </GenericModal>
  );
}
